import { createContext, useContext, useEffect, useState } from 'react';
import * as authService from '../services/authService';
import * as entitlementService from '../services/entitlementService';
import * as userService from '../services/userService';
import {
  claimUnownedLocalProgress,
  loadServerPrimaryDashboard,
} from '../services/progressSyncService';
import { readJson, writeJson } from '../utils/storage';
import {
  getOfflinePremiumStatus, offlineAccessProfile, supportsPremiumOffline,
  clearOfflinePremium,
} from '../services/offlinePremiumService';

const UserContext = createContext();

const FREE_ACCESS_PROFILE = {
  tier: 'free',
  premiumActive: false,
  premiumExpiresAt: null,
  entitlements: [],
  subscription: null,
};

const USER_CACHE_PREFIX = 'concours_bf_user_cache_v1';

function userCacheKey(userId) {
  return `${USER_CACHE_PREFIX}:${userId}`;
}

function normalizeCachedAccess(access) {
  if (!access?.premiumActive) {
    return access || FREE_ACCESS_PROFILE;
  }

  const expiry = access.premiumExpiresAt
    ? new Date(access.premiumExpiresAt).getTime()
    : null;

  if (Number.isFinite(expiry) && expiry <= Date.now()) {
    return FREE_ACCESS_PROFILE;
  }

  return access;
}

function cacheAuthenticatedUser(user) {
  if (!user?.id) return;
  writeJson(userCacheKey(user.id), {
    ...user,
    access: normalizeCachedAccess(user.access),
    cachedAt: new Date().toISOString(),
  });
}

function getCachedAuthenticatedUser(userId) {
  if (!userId) return null;
  const cached = readJson(userCacheKey(userId), null);
  if (!cached || String(cached.id) !== String(userId)) return null;

  return {
    ...cached,
    access: normalizeCachedAccess(cached.access),
  };
}


function clearAuthenticatedSession(userId = null) {
  const resolvedUserId =
    userId || localStorage.getItem('concours_bf_current_user');

  localStorage.removeItem('concours_bf_token');
  localStorage.removeItem('concours_bf_current_user');

  if (resolvedUserId) {
    localStorage.removeItem(userCacheKey(resolvedUserId));
  }
}

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [accessProfile, setAccessProfile] = useState(FREE_ACCESS_PROFILE);
  const [loading, setLoading] = useState(true);

  // Au démarrage, vérifier si un token JWT existe et récupérer l'utilisateur via /api/me
  useEffect(() => {
    const token = localStorage.getItem('concours_bf_token');
    if (token) {
      authService.getMe()
        .then((user) => {
          localStorage.setItem('concours_bf_current_user', user.id);

          // Legacy unowned browser history is claimed at most once. The
          // ownership marker prevents another account on the same browser
          // from silently receiving that history later.
          claimUnownedLocalProgress(user.id);
          cacheAuthenticatedUser(user);
          setCurrentUser(user);
          setAccessProfile(user.access || FREE_ACCESS_PROFILE);
          loadServerPrimaryDashboard(user.id).catch((error) => {
            console.warn('Synchronisation de progression différée.', error);
          });
        })
        .catch(async (error) => {
          // Uniquement un refus d'authentification invalide définitivement la session.
          if (error?.status === 401 || error?.status === 403) {
            clearAuthenticatedSession();
            setCurrentUser(null);
            setAccessProfile(FREE_ACCESS_PROFILE);
            return;
          }

          // Une panne réseau/API temporaire conserve l'identité mise en cache,
          // sans prolonger artificiellement un entitlement déjà expiré.
          const cachedUserId = localStorage.getItem('concours_bf_current_user');
          const cachedUser = getCachedAuthenticatedUser(cachedUserId);
          if (cachedUser) {
            // Browser cache alone must NOT grant offline Premium on Android.
            const local = supportsPremiumOffline()
              ? offlineAccessProfile(await getOfflinePremiumStatus(cachedUser.id))
              : cachedUser.access;
            const safeAccess = local || FREE_ACCESS_PROFILE;
            setCurrentUser({ ...cachedUser, access: safeAccess });
            setAccessProfile(safeAccess);
            loadServerPrimaryDashboard(cachedUser.id).catch(() => {});
          } else {
            setCurrentUser(null);
            setAccessProfile(FREE_ACCESS_PROFILE);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setAccessProfile(FREE_ACCESS_PROFILE);
      setLoading(false);
    }
  }, []);

  // Re-evaluate time-limited Premium access while the application stays open.
  // Long subscriptions are checked at least daily; near expiry we refresh just
  // after the entitlement boundary so stale UI cannot keep Premium content open.
  useEffect(() => {
    if (!currentUser || !accessProfile?.premiumActive) return undefined;

    const expiresAt = accessProfile.premiumExpiresAt
      ? new Date(accessProfile.premiumExpiresAt).getTime()
      : null;

    const dailyCheckMs = 24 * 60 * 60 * 1000;
    const untilExpiry =
      Number.isFinite(expiresAt)
        ? Math.max(1000, expiresAt - Date.now() + 1000)
        : dailyCheckMs;
    const delay = Math.min(dailyCheckMs, untilExpiry);

    const timeout = setTimeout(() => {
      // A signed local license has an absolute expiry. Do not wait for the
      // network timeout (20 s) when the paid period ends while offline.
      if (Number.isFinite(expiresAt) && Date.now() >= expiresAt) {
        setAccessProfile(FREE_ACCESS_PROFILE);
        setCurrentUser((user) => user ? { ...user, access: FREE_ACCESS_PROFILE } : null);
        return;
      }
      refreshAccessProfile().catch((error) => {
        console.warn('Rafraîchissement des droits différé.', error);
      });
    }, delay);

    return () => clearTimeout(timeout);
  // refreshAccessProfile is a stable function declaration within this provider.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser?.id,
    accessProfile?.premiumActive,
    accessProfile?.premiumExpiresAt,
  ]);

  // Once a signed package has been downloaded, guard the current Premium view
  // with the native vault immediately, without waiting for an API timeout.
  useEffect(() => {
    if (!currentUser?.id || !supportsPremiumOffline()) return undefined;
    let mounted = true;
    const activate = async () => {
      const status = await getOfflinePremiumStatus(currentUser.id);
      if (!mounted || !status.available) return;
      setAccessProfile((old) => old.premiumActive
        ? { ...old, offline: true, premiumExpiresAt: status.expiresAt }
        : old);
    };
    window.addEventListener('concoursbf:offline-package-ready', activate);
    return () => {
      mounted = false;
      window.removeEventListener('concoursbf:offline-package-ready', activate);
    };
  }, [currentUser?.id]);

  // Periodic native verification also detects expiry or clock rollback while
  // a downloaded Premium lesson is already on screen.
  useEffect(() => {
    if (!currentUser?.id || !supportsPremiumOffline()) return undefined;
    let alive = true;
    let running = false;
    const validate = async () => {
      if (running) return;
      running = true;
      try {
        const status = await getOfflinePremiumStatus(currentUser.id);
        if (!alive) return;
        // The local vault only governs offline sessions. Online subscriptions
        // continue to be governed by the server's entitlement response.
        if (accessProfile?.offline && !status.available) {
          setAccessProfile(FREE_ACCESS_PROFILE);
          setCurrentUser((user) => user ? { ...user, access: FREE_ACCESS_PROFILE } : null);
        }
      } finally { running = false; }
    };
    const interval = setInterval(validate, 2000);
    const onFocus = () => { validate(); };
    window.addEventListener('focus', onFocus);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [currentUser?.id, accessProfile?.offline]);

  // Revalidate the cached authenticated identity as soon as connectivity returns.
  useEffect(() => {
    const handleOnline = () => {
      const token = localStorage.getItem('concours_bf_token');
      if (!token) return;

      authService.getMe()
        .then((user) => {
          localStorage.setItem('concours_bf_current_user', user.id);
          cacheAuthenticatedUser(user);
          setCurrentUser(user);
          setAccessProfile(user.access || FREE_ACCESS_PROFILE);
          loadServerPrimaryDashboard(user.id).catch((error) => {
            console.warn('Synchronisation de progression différée.', error);
          });
        })
        .catch((error) => {
          if (error?.status === 401 || error?.status === 403) {
            clearAuthenticatedSession();
            setCurrentUser(null);
            setAccessProfile(FREE_ACCESS_PROFILE);
          }
        });
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Inscription
  async function signup(
    email,
    password,
    pseudonym,
    lastName,
    firstName,
    acceptTerms,
    acceptPrivacy,
    referralCode = ''
  ) {
    const data = await authService.register({
      email,
      password,
      pseudonym,
      lastName,
      firstName,
      acceptTerms,
      acceptPrivacy,
      referralCode,
    });
    if (data.token) {
      localStorage.setItem('concours_bf_token', data.token);
    }
    localStorage.setItem('concours_bf_current_user', data.id);
    const { token, ...user } = data;
    claimUnownedLocalProgress(user.id);
    cacheAuthenticatedUser(user);
    setCurrentUser(user);
    setAccessProfile(user.access || FREE_ACCESS_PROFILE);
    loadServerPrimaryDashboard(user.id).catch((error) => {
      console.warn('Synchronisation de progression différée.', error);
    });
    return user;
  }

  // Connexion
  async function login(email, password) {
    const data = await authService.login(email, password);
    if (data.token) {
      localStorage.setItem('concours_bf_token', data.token);
    }
    localStorage.setItem('concours_bf_current_user', data.id);
    const { token, ...user } = data;
    claimUnownedLocalProgress(user.id);
    cacheAuthenticatedUser(user);
    setCurrentUser(user);
    setAccessProfile(user.access || FREE_ACCESS_PROFILE);
    loadServerPrimaryDashboard(user.id).catch((error) => {
      console.warn('Synchronisation de progression différée.', error);
    });
    return user;
  }

  // Déconnexion
  async function logout() {
    try {
      await authService.logout();
    } catch (error) {
      // Local logout must still succeed if the API is temporarily unavailable
      // or if the server has already revoked the session.
      console.warn('Déconnexion serveur différée.', error);
    }

    clearAuthenticatedSession(currentUser?.id);
    // Remove the encrypted offline copy on explicit logout (another account
    // on the same shared device must not inherit a previous subscription).
    await clearOfflinePremium().catch(() => {});
    setCurrentUser(null);
    setAccessProfile(FREE_ACCESS_PROFILE);

    // Future anonymous activity after logout may belong to a different person
    // on the same browser. Already-claimed history remains user-scoped.
    localStorage.removeItem('progress_legacy_owner_v1');
  }

  // Mettre à jour le profil
  async function updateProfile(updates) {
    if (!currentUser) return null;
    const data = await userService.updateUser(currentUser.id, updates);
    if (data?.requiresRelogin) {
      clearAuthenticatedSession(currentUser.id);
      setCurrentUser(null);
      setAccessProfile(FREE_ACCESS_PROFILE);
      return data;
    }

    const nextUser = {
      ...currentUser,
      ...data,
      access: data.access || currentUser.access || {
        tier: 'free',
        premiumActive: false,
        premiumExpiresAt: null,
        entitlements: [],
        subscription: null,
      },
    };
    cacheAuthenticatedUser(nextUser);
    setCurrentUser(nextUser);
    return nextUser;
  }

  async function refreshAccessProfile() {
    if (!currentUser) {
      setAccessProfile(FREE_ACCESS_PROFILE);
      return FREE_ACCESS_PROFILE;
    }

    try {
      const access = await entitlementService.getAccessProfile();
      const nextAccess = access || FREE_ACCESS_PROFILE;
      setAccessProfile(nextAccess);
      setCurrentUser((user) => {
        if (!user) return user;
        const nextUser = { ...user, access: nextAccess };
        cacheAuthenticatedUser(nextUser);
        return nextUser;
      });
      return nextAccess;
    } catch (error) {
      if (error?.status === 401 || error?.status === 403) {
        clearAuthenticatedSession(currentUser.id);
        setCurrentUser(null);
        setAccessProfile(FREE_ACCESS_PROFILE);
        throw error;
      }
      if (supportsPremiumOffline()) {
        const verified = offlineAccessProfile(await getOfflinePremiumStatus(currentUser.id));
        const safe = verified || FREE_ACCESS_PROFILE;
        setAccessProfile(safe);
        setCurrentUser((user) => user ? { ...user, access: safe } : null);
        return safe;
      }
      throw error;
    }
  }

  async function exportMyData() {
    if (!currentUser) return null;
    return userService.exportUser(currentUser.id);
  }

  async function requestAccountDeletion(reason = '') {
    if (!currentUser) return null;
    const result = await userService.requestDeletion(currentUser.id, reason);
    clearAuthenticatedSession(currentUser.id);
    setCurrentUser(null);
    setAccessProfile(FREE_ACCESS_PROFILE);
    return result;
  }

  async function resendEmailVerification() {
    const result = await authService.resendEmailVerification();
    return result;
  }

  // Réinitialiser le mot de passe (envoi d'email)
  async function resetPassword(email) {
    return authService.resetPassword(email);
  }

  const value = {
    currentUser,
    accessProfile,
    loading,
    signup,
    login,
    logout,
    updateProfile,
    exportMyData,
    requestAccountDeletion,
    resendEmailVerification,
    resetPassword,
    refreshAccessProfile
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}
